// components/RichTextEditor/RichTextEditor.tsx
// Main rich text editor component using Quill

import React, { useRef, useMemo, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './RichTextEditor.css';
import {
    getQuillConfig,
    IMAGE_CONFIG
} from './editorConfig';
import { getWordCountString, estimateReadingTime } from '../../utils/wordCounter';
import { compressInlineImage, validateImageFile } from '../../utils/imageCompression';

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    onImageUpload?: (file: File) => Promise<string>;
    simple?: boolean;
    showWordCount?: boolean;
    minHeight?: string;
    maxHeight?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
                                                                  value,
                                                                  onChange,
                                                                  placeholder,
                                                                  disabled = false,
                                                                  onImageUpload,
                                                                  simple = false,
                                                                  showWordCount = true,
                                                                  minHeight = '300px',
                                                                  maxHeight = '600px',
                                                              }) => {
    const quillRef = useRef<ReactQuill>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Get Quill configuration
    const quillConfig = useMemo(
        () => getQuillConfig({ simple, placeholder }),
        [simple, placeholder]
    );

    // Custom image handler
    const imageHandler = useCallback(() => {
        // Trigger file input click
        fileInputRef.current?.click();
    }, []);

    // Handle image file selection
// Handle image file selection
    const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Reset file input
        e.target.value = '';

        try {
            // Validate image
            const validation = await validateImageFile(file);
            if (!validation.isValid) {
                alert(validation.message);
                return;
            }

            // Check if onImageUpload handler is provided
            if (!onImageUpload) {
                alert('Image upload is not configured');
                return;
            }

            // Get editor and selection
            const editor = quillRef.current?.getEditor();
            if (!editor) return;

            const range = editor.getSelection(true);
            const cursorPosition = range ? range.index : editor.getLength();

            // Insert loading text
            editor.insertText(cursorPosition, '⏳ Uploading image...');
            editor.setSelection({ index: cursorPosition, length: 0 });

            // Compress image
            const compressedFile = await compressInlineImage(file);

            // Upload image
            const imageUrl = await onImageUpload(compressedFile);

            // Remove loading text
            editor.deleteText(cursorPosition, '⏳ Uploading image...'.length);

            // Insert image at the saved position
            editor.insertEmbed(cursorPosition, 'image', imageUrl);

            // Insert a line break after the image for easier typing
            editor.insertText(cursorPosition + 1, '\n');

            // Move cursor AFTER the line break (use setTimeout to ensure it happens after Quill updates)
            setTimeout(() => {
                editor.setSelection({ index: cursorPosition + 2, length: 0 });
            }, 10);

        } catch (error: any) {
            console.error('Image upload failed:', error);
            alert(error.message || 'Failed to upload image. Please try again.');

            // Remove loading text if still present
            const editor = quillRef.current?.getEditor();
            if (editor) {
                const currentContent = editor.getText();
                if (currentContent.includes('⏳ Uploading image...')) {
                    const index = currentContent.indexOf('⏳ Uploading image...');
                    editor.deleteText(index, '⏳ Uploading image...'.length);
                }
            }
        }
    };

    // Customize Quill modules with image handler
    const modules = useMemo(() => ({
        ...quillConfig.modules,
        toolbar: {
            container: quillConfig.modules.toolbar,
            handlers: {
                image: imageHandler,
            },
        },
    }), [quillConfig.modules, imageHandler]);

    // Handle content change
    const handleChange = (content: string) => {
        onChange(content);
    };

    // Get word count metrics
    const wordCount = useMemo(() => {
        if (!showWordCount || !value) return null;
        return {
            words: getWordCountString(value),
            readingTime: `${estimateReadingTime(value)} min read`,
        };
    }, [value, showWordCount]);

    return (
        <div className="rich-text-editor-container">
            {/* Hidden file input for image uploads */}
            <input
                ref={fileInputRef}
                type="file"
                accept={IMAGE_CONFIG.allowedTypes.join(',')}
                style={{ display: 'none' }}
                onChange={handleImageSelect}
            />

            {/* Quill Editor */}
            <div
                className="rich-text-editor-wrapper"
                style={{
                    minHeight,
                    maxHeight,
                }}
            >
                <ReactQuill
                    ref={quillRef}
                    theme={quillConfig.theme}
                    value={value}
                    onChange={handleChange}
                    modules={modules}
                    formats={quillConfig.formats}
                    placeholder={quillConfig.placeholder}
                    readOnly={disabled}
                    bounds={quillConfig.bounds}
                    preserveWhitespace
                />
            </div>

            {/* Word count display */}
            {showWordCount && wordCount && (
                <div className="editor-metrics">
                    <span className="metric-item">
                        📝 {wordCount.words}
                    </span>
                    <span className="metric-separator">•</span>
                    <span className="metric-item">
                        ⏱️ {wordCount.readingTime}
                    </span>
                </div>
            )}
        </div>
    );
};

export default RichTextEditor;